import { Injectable, signal, computed, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CartItem, ShopProduct, FulfilmentMethod, PickupLocation } from '../models/shop.model';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly STORAGE_KEY = 'colorfol_cart';

  cartItems = signal<CartItem[]>([]);

  // Fulfilment
  fulfilmentMethod = signal<FulfilmentMethod>('delivery');
  pickupLocationId = signal<number | null>(null);
  selectedPickupLocation = signal<PickupLocation | null>(null);
  shippingFee = signal<number>(0);

  cartCount = computed(() =>
    this.cartItems().reduce((sum, item) => sum + item.quantity, 0)
  );

  cartSubtotal = computed(() =>
    this.cartItems().reduce((sum, item) => {
      const price = item.isBundle ? item.bundle!.price : item.product!.price;
      return sum + (price * item.quantity);
    }, 0)
  );

  cartTotal = computed(() =>
    this.cartSubtotal() + this.shippingFee()
  );

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.loadFromStorage();
    }
  }

  addToCart(product: ShopProduct, quantity: number = 1, selectedSize?: string, selectedColor?: string, variant_id?: number): void {
    const items = [...this.cartItems()];
    
    // Empêcher le mixage de bundles et de produits standards
    if (items.some(i => i.isBundle)) {
      alert("Vous ne pouvez pas mélanger des packs (bundles) et des articles standards dans la même commande. Le panier a été vidé pour ajouter cet article.");
      this.clearCart();
      return this.addToCart(product, quantity, selectedSize, selectedColor, variant_id);
    }

    // Utiliser le variant_id comme clé d'unicité s'il existe, sinon fallback
    const id = variant_id ? `variant_${variant_id}` : `prod_${product.id}_${selectedSize || 'none'}_${selectedColor || 'none'}`;
    const existingIndex = items.findIndex(item => item.id === id);

    if (existingIndex >= 0) {
      items[existingIndex] = { ...items[existingIndex], quantity: items[existingIndex].quantity + quantity };
    } else {
      items.push({ id, product, quantity, selectedSize, selectedColor, variant_id });
    }

    this.cartItems.set(items);
    this.saveToStorage();
  }

  addBundleToCart(bundle: any, quantity: number = 1, selectedSizes?: { [productId: number]: string }): void {
    const items = [...this.cartItems()];

    // Empêcher le mixage de bundles et de produits standards
    if (items.some(i => !i.isBundle)) {
      alert("Vous ne pouvez pas mélanger des packs (bundles) et des articles standards dans la même commande. Le panier a été vidé pour ajouter ce pack.");
      this.clearCart();
      return this.addBundleToCart(bundle, quantity, selectedSizes);
    }

    const sizesKey = selectedSizes ? JSON.stringify(selectedSizes) : 'none';
    const id = `bundle_${bundle.id}_${sizesKey}`;
    const existingIndex = items.findIndex(item => item.id === id);

    if (existingIndex >= 0) {
      items[existingIndex] = { ...items[existingIndex], quantity: items[existingIndex].quantity + quantity };
    } else {
      items.push({ id, isBundle: true, bundle, quantity, selectedSizes });
    }

    this.cartItems.set(items);
    this.saveToStorage();
  }

  removeFromCart(id: string): void {
    const items = this.cartItems().filter(item => item.id !== id);
    this.cartItems.set(items);
    this.saveToStorage();
  }

  updateQuantity(id: string, quantity: number): void {
    if (quantity <= 0) {
      this.removeFromCart(id);
      return;
    }

    const items = this.cartItems().map(item => {
      if (item.id === id) {
        return { ...item, quantity };
      }
      return item;
    });

    this.cartItems.set(items);
    this.saveToStorage();
  }

  setFulfilmentMethod(method: FulfilmentMethod): void {
    this.fulfilmentMethod.set(method);
    if (method === 'pickup') {
      // Pas de frais de livraison en retrait
      this.shippingFee.set(0);
    }
  }

  setPickupLocation(location: PickupLocation): void {
    this.pickupLocationId.set(location.id);
    this.selectedPickupLocation.set(location);
  }

  setShippingFee(fee: number): void {
    this.shippingFee.set(fee);
  }

  clearCart(): void {
    this.cartItems.set([]);
    this.fulfilmentMethod.set('delivery');
    this.pickupLocationId.set(null);
    this.selectedPickupLocation.set(null);
    this.shippingFee.set(0);
    this.saveToStorage();
  }

  private saveToStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.cartItems()));
      } catch (e) {
        console.error('Error saving cart to localStorage', e);
      }
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const items = JSON.parse(stored) as CartItem[];
        this.cartItems.set(items);
      }
    } catch (e) {
      console.error('Error loading cart from localStorage', e);
    }
  }
}
